// Admin page: people & roles, the activity (audit) log, and settings that
// used to be hard-coded. Only shown to admins; every action here is also
// enforced on the server.
import { Api } from "../../platform/api.js";
import { Cap } from "../data.jsx";
import { useT } from "../i18n.jsx";
import { WEEKDAYS } from "../model.jsx";
import { DEFAULT_SETTINGS, applySettings, validStagePresets } from "../settings.jsx";
import { KeyIcon, LogOutIcon, SettingsIcon, T, UserPlusIcon, UsersIcon, HistoryIcon } from "../theme.jsx";
import { toast } from "../team/collab.jsx";
import { Button, Field, Modal, Notice, fmtTime, inputStyle } from "../team/ui.jsx";

const { useCallback, useEffect, useState } = React;

const ROLE_ORDER = ["admin", "producer", "onground", "broadcast", "viewer"];
const AUDIT_COLLECTIONS = ["boards", "masters", "templates", "photoLibrary", "cuePool", "sponsors", "settings", "_users"];

function AdminScreen({ viewer, authConfig, onSettingsChanged }) {
  const { t } = useT();
  const [tab, setTab] = useState("users");
  const tabs = [
    { key: "users", label: t("adminUsers"), icon: UsersIcon },
    { key: "audit", label: t("adminAudit"), icon: HistoryIcon },
    { key: "settings", label: t("adminSettings"), icon: SettingsIcon },
  ];
  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      <h1 style={{ fontSize: 20, fontWeight: 900, margin: "4px 0 14px", color: T.text }}>{t("navAdmin")}</h1>
      <div role="tablist" aria-label={t("navAdmin")} style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {tabs.map((x) => (
          <button key={x.key} role="tab" id={`admin-tab-${x.key}`} aria-selected={tab === x.key} aria-controls={`admin-panel-${x.key}`}
            className="rb-btn" type="button" onClick={() => setTab(x.key)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 8, border: `1px solid ${tab === x.key ? T.accent : T.border}`, background: tab === x.key ? T.accent : T.panel, color: T.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
            <x.icon size={15} /> {x.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`admin-panel-${tab}`} aria-labelledby={`admin-tab-${tab}`}>
        {tab === "users" && <UsersPanel viewer={viewer} authConfig={authConfig} />}
        {tab === "audit" && <AuditPanel />}
        {tab === "settings" && <SettingsPanel onSettingsChanged={onSettingsChanged} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- people ----
function UsersPanel({ viewer, authConfig }) {
  const { t, lang } = useT();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const load = useCallback(async () => {
    try { setUsers((await Api.listUsers()).users); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (u, patch, okText) => {
    setError("");
    try { await Api.updateUser(u.id, patch); await load(); toast(okText || t("done")); }
    catch (e) { setError(e.message); }
  };
  const cell = { padding: "9px 10px", borderBottom: `1px solid ${T.border}`, fontSize: 13, verticalAlign: "middle", textAlign: "left" };
  // people who signed up themselves and are waiting come first
  const pendingCount = (users || []).filter((u) => u.pending).length;
  const rows = users && [...users].sort((a, b) => (b.pending ? 1 : 0) - (a.pending ? 1 : 0));
  return (
    <section>
      {error && <Notice>{error}</Notice>}
      {pendingCount > 0 && <Notice tone="info">{t("pendingCount").replace("{n}", String(pendingCount))}</Notice>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <details style={{ fontSize: 13, color: T.subtext, maxWidth: 640 }}>
          <summary style={{ cursor: "pointer", fontWeight: 700, color: T.text }}>{t("userRole")}</summary>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
            {ROLE_ORDER.map((r) => <li key={r}><b>{t(`role_${r}`)}</b> — {t(`roleDesc_${r}`)}</li>)}
          </ul>
        </details>
        <Button onClick={() => setAdding(true)} style={{ display: "flex", alignItems: "center", gap: 6 }}><UserPlusIcon size={15} /> {t("addUser")}</Button>
      </div>
      <div style={{ overflowX: "auto", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <caption style={{ position: "absolute", left: -9999 }}>{t("adminUsers")}</caption>
          <thead>
            <tr style={{ background: T.panel2 }}>
              {[t("nameLabel"), t("userRole"), t("signInTitle"), t("lastLogin"), ""].map((h, i) => <th key={i} scope="col" style={{ ...cell, fontWeight: 800 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr><td colSpan={5} style={cell}>{t("loading")}</td></tr>
            ) : rows.map((u) => (
              <tr key={u.id} style={{ opacity: u.disabled && !u.pending ? 0.6 : 1, background: u.pending ? T.infoBg : undefined }}>
                <td style={cell}>
                  <div style={{ fontWeight: 700 }}>{u.name} {u.id === viewer.id && <span style={{ color: T.subtext, fontWeight: 400 }}>{t("you")}</span>}</div>
                  <div style={{ color: T.subtext, fontSize: 12.5 }}>{u.email}</div>
                  {u.pending ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                      <span style={{ color: T.infoText, fontSize: 12.5, fontWeight: 700 }}>{t("statusPending")}</span>
                      <Button onClick={() => update(u, { disabled: false })} style={{ padding: "5px 12px", fontSize: 12.5 }}>{t("approveUser")}</Button>
                    </div>
                  ) : u.disabled && <div style={{ color: T.danger, fontSize: 12.5, fontWeight: 700 }}>{t("statusDisabled")}</div>}
                </td>
                <td style={cell}>
                  <select aria-label={`${t("userRole")}: ${u.name}`} value={u.role} disabled={u.id === viewer.id}
                    onChange={(e) => update(u, { role: e.target.value })}
                    style={{ ...inputStyle, width: "auto", padding: "7px 9px", fontSize: 13 }}>
                    {ROLE_ORDER.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                  </select>
                </td>
                <td style={cell}>{[u.hasPassword && t("methodPassword"), u.google && t("methodGoogle")].filter(Boolean).join(" · ") || "—"}</td>
                <td style={cell}>{u.lastLoginAt ? fmtTime(u.lastLoginAt, lang) : t("never")}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <Button variant="secondary" onClick={() => setPwFor(u)} style={{ padding: "6px 10px", fontSize: 12.5, display: "flex", gap: 5, alignItems: "center" }}><KeyIcon size={13} /> {t("resetPassword")}</Button>
                    <Button variant="secondary" onClick={() => update(u, { signOutEverywhere: true })} style={{ padding: "6px 10px", fontSize: 12.5, display: "flex", gap: 5, alignItems: "center" }}><LogOutIcon size={13} /> {t("signOutEverywhere")}</Button>
                    {!u.pending && u.id !== viewer.id && (
                      <Button variant={u.disabled ? "secondary" : "danger"} onClick={() => update(u, { disabled: !u.disabled })} style={{ padding: "6px 10px", fontSize: 12.5 }}>
                        {u.disabled ? t("enableUser") : t("disableUser")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <AddUserModal authConfig={authConfig} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); toast(t("done")); }} />}
      {pwFor && <SetPasswordModal user={pwFor} onClose={() => setPwFor(null)} onSave={(pw) => { const u = pwFor; setPwFor(null); update(u, { password: pw }, t("passwordChanged")); }} />}
    </section>
  );
}

function AddUserModal({ authConfig, onClose, onDone }) {
  const { t } = useT();
  const [f, setF] = useState({ email: "", name: "", role: "viewer", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await Api.createUser({ ...f, password: f.password || undefined }); onDone(); }
    catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal title={t("addUser")} onClose={onClose} closeLabel={t("close")} width={460}>
      {error && <Notice>{error}</Notice>}
      <form onSubmit={submit}>
        <Field label={t("emailLabel")} inputProps={{ type: "email", value: f.email, onChange: set("email") }} />
        <Field label={t("nameLabel")} inputProps={{ value: f.name, onChange: set("name") }} />
        <Field label={t("userRole")} hint={t(`roleDesc_${f.role}`)}>
          {(a11y) => (
            <select {...a11y} value={f.role} onChange={set("role")} style={inputStyle}>
              {ROLE_ORDER.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
            </select>
          )}
        </Field>
        <Field label={t("initialPassword")} hint={authConfig && authConfig.google ? t("initialPasswordHint") : t("passwordHint")}
          inputProps={{ type: "password", autoComplete: "new-password", value: f.password, onChange: set("password") }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit" disabled={busy || !f.email || (!f.password && !(authConfig && authConfig.google))}>{t("create")}</Button>
        </div>
      </form>
    </Modal>
  );
}

function SetPasswordModal({ user, onClose, onSave }) {
  const { t } = useT();
  const [pw, setPw] = useState("");
  return (
    <Modal title={`${t("resetPassword")} — ${user.name}`} onClose={onClose} closeLabel={t("close")} width={420}>
      <form onSubmit={(e) => { e.preventDefault(); if (pw.length >= 10) onSave(pw); }}>
        <Field label={t("newPassword")} hint={t("passwordHint")} inputProps={{ type: "password", autoComplete: "new-password", value: pw, onChange: (e) => setPw(e.target.value) }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit" disabled={pw.length < 10}>{t("save")}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------- activity log ----
function actionLabel(a, t) {
  if (!a) return "";
  if (a.startsWith("restore:")) return `${t("act_restore")} ${a.slice(8)}`;
  const k = `act_${a}`;
  const v = t(k);
  return v === k ? a : v;
}

function AuditPanel() {
  const { t, lang } = useT();
  const [entries, setEntries] = useState(null);
  const [more, setMore] = useState(false);
  const [filter, setFilter] = useState({ collection: "", actor: "" });
  const [people, setPeople] = useState([]);
  const [error, setError] = useState("");
  const PAGE = 100;
  const load = useCallback(async (before) => {
    try {
      const r = await Api.audit({ limit: PAGE, before, collection: filter.collection, actor: filter.actor });
      setEntries((prev) => (before && prev ? [...prev, ...r.entries] : r.entries));
      setMore(r.entries.length === PAGE);
    } catch (e) { setError(e.message); }
  }, [filter]);
  useEffect(() => { setEntries(null); load(); }, [load]);
  useEffect(() => { Api.listUsers().then((r) => setPeople(r.users)).catch(() => {}); }, []);
  const cell = { padding: "8px 10px", borderBottom: `1px solid ${T.border}`, fontSize: 13, textAlign: "left", verticalAlign: "top" };
  return (
    <section>
      {error && <Notice>{error}</Notice>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          {t("auditItem")}
          <select value={filter.collection} onChange={(e) => setFilter((f) => ({ ...f, collection: e.target.value }))} style={{ ...inputStyle, width: "auto", padding: "7px 9px", fontSize: 13 }}>
            <option value="">{t("auditAllCollections")}</option>
            {AUDIT_COLLECTIONS.map((c) => <option key={c} value={c}>{t(`col_${c}`)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          {t("auditWho")}
          <select value={filter.actor} onChange={(e) => setFilter((f) => ({ ...f, actor: e.target.value }))} style={{ ...inputStyle, width: "auto", padding: "7px 9px", fontSize: 13 }}>
            <option value="">{t("auditAllPeople")}</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      <div style={{ overflowX: "auto", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
          <caption style={{ position: "absolute", left: -9999 }}>{t("adminAudit")}</caption>
          <thead>
            <tr style={{ background: T.panel2 }}>
              {[t("auditWhen"), t("auditWho"), t("auditWhat"), t("auditItem"), t("fieldsChanged")].map((h) => <th key={h} scope="col" style={{ ...cell, fontWeight: 800 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {entries === null ? <tr><td colSpan={5} style={cell}>{t("loading")}</td></tr>
              : entries.length === 0 ? <tr><td colSpan={5} style={cell}>{t("auditEmpty")}</td></tr>
              : entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>{fmtTime(e.ts, lang)}</td>
                  <td style={cell}>{e.actor ? e.actor.name : <i>{t("system")}</i>}{e.detail && e.detail.email ? <div style={{ color: T.subtext, fontSize: 12 }}>{e.detail.email}</div> : null}</td>
                  <td style={{ ...cell, color: e.action === "auth.login_failed" ? T.danger : T.text, fontWeight: 600 }}>{actionLabel(e.action, t)}</td>
                  <td style={cell}>{e.collection ? `${t(`col_${e.collection}`)}${e.docId && e.collection !== "_users" ? ` · ${e.docId}` : ""}` : "—"}</td>
                  <td style={{ ...cell, color: T.subtext, fontSize: 12.5 }}>{(e.fields || []).filter((f) => f !== "passwordHash").join(", ")}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {more && entries && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <Button variant="secondary" onClick={() => load(entries[entries.length - 1].ts)}>{t("loadMore")}</Button>
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------- settings ----
function SettingsPanel({ onSettingsChanged }) {
  const { t, lang } = useT();
  const [s, setS] = useState(null);
  const [presetsText, setPresetsText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = () => Cap.db.collection("settings").doc("app");
  const fill = (data) => {
    const merged = { ...DEFAULT_SETTINGS, ...(data || {}) };
    setS(merged);
    setPresetsText(JSON.stringify(merged.stagePresets, null, 2));
  };
  const load = useCallback(async () => {
    try { const snap = await ref().get(); fill(snap.exists ? snap.data() : null); } catch (e) { setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const on = (e) => { if (e.detail && e.detail.col === "settings") load(); };
    window.addEventListener("rundown:reload-doc", on);
    return () => window.removeEventListener("rundown:reload-doc", on);
  }, [load]);
  if (!s) return <div style={{ color: T.subtext }}>{t("loading")}</div>;

  const setField = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
  const setWd = (id, k, v) => setS((p) => ({ ...p, weekdays: p.weekdays.map((w) => (w.id === id ? { ...w, [k]: v } : w)) }));
  let presetsValid = true;
  try { presetsValid = validStagePresets(JSON.parse(presetsText)); } catch (e) { presetsValid = false; }

  const save = async () => {
    setError("");
    if (!presetsValid) { setError(t("invalidJson")); return; }
    const next = { ...s, stagePresets: JSON.parse(presetsText), updatedAt: Date.now() };
    setBusy(true);
    try {
      await ref().set(next);
      applySettings(next);
      onSettingsChanged(next);
      toast(t("settingsSaved"));
    } catch (e) {
      if (e.code !== "conflict") setError(e.message);
    }
    setBusy(false);
  };
  const box = { background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, marginBottom: 14 };
  return (
    <section>
      {error && <Notice>{error}</Notice>}
      <div style={box}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 10px" }}>{t("settingsBranding")}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          <Field label={t("venueNameTh")} hint={t("venueName")} inputProps={{ value: s.venueNameTh, onChange: setField("venueNameTh"), placeholder: "เวทีมวยราชดำเนิน" }} />
          <Field label={t("venueNameEn")} inputProps={{ value: s.venueNameEn, onChange: setField("venueNameEn"), placeholder: "Rajadamnern Stadium" }} />
          <Field label={t("orgNameLabel")} inputProps={{ value: s.orgName, onChange: setField("orgName") }} />
        </div>
      </div>
      <div style={box}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 10px" }}>{t("settingsWeekdays")}</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead><tr>{["", t("wdName"), t("wdCategory"), t("wdCode")].map((h, i) => <th key={i} scope="col" style={{ textAlign: "left", fontSize: 13, padding: 6 }}>{h}</th>)}</tr></thead>
            <tbody>
              {s.weekdays.map((w) => {
                const label = (WEEKDAYS.find((x) => x.id === w.id) || {})[lang === "en" ? "labelEn" : "label"];
                return (
                  <tr key={w.id}>
                    <th scope="row" style={{ textAlign: "left", fontSize: 13, padding: 6, whiteSpace: "nowrap" }}>{label}</th>
                    {["name", "category", "code"].map((k) => (
                      <td key={k} style={{ padding: 4 }}>
                        <input aria-label={`${label} — ${t(k === "name" ? "wdName" : k === "category" ? "wdCategory" : "wdCode")}`} value={w[k] || ""} onChange={(e) => setWd(w.id, k, e.target.value)} style={{ ...inputStyle, padding: "7px 9px", fontSize: 13 }} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div style={box}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 6px" }}>{t("settingsStagePresets")}</h2>
        <Field label={t("settingsStagePresets")} hint={t("stagePresetsHint")} error={presetsValid ? "" : t("invalidJson")}>
          {(a11y) => (
            <textarea {...a11y} rows={12} value={presetsText} onChange={(e) => setPresetsText(e.target.value)} spellCheck={false}
              style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 }} />
          )}
        </Field>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button variant="ghost" onClick={() => fill(null)}>{t("resetDefaults")}</Button>
        <Button disabled={busy || !presetsValid} onClick={save}>{t("saveSettings")}</Button>
      </div>
    </section>
  );
}

export { AdminScreen };
